import React, { useRef, useState } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Loader2,
  Paperclip,
  Music,
  Video,
  Image as ImageIcon,
  FileText,
  X,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../RichTextEditor";
import Avatar from "./Avatar";
import { createMyPost, uploadToR2 } from "../../lib/studioApi";

const MAX_SIZE_BY_KIND = {
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  image: 20 * 1024 * 1024,
  file: 100 * 1024 * 1024,
};

function makePendingId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function detectKind(file) {
  const t = (file.type || "").toLowerCase();
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("image/")) return "image";
  return "file";
}

function KindIcon({ kind, className = "w-4 h-4" }) {
  if (kind === "audio") return <Music className={className} />;
  if (kind === "video") return <Video className={className} />;
  if (kind === "image") return <ImageIcon className={className} />;
  return <FileText className={className} />;
}

/**
 * UserComposeBox — artist-side post composer for the Studio Home feed.
 * Posts are private to the artist + admin (server-enforced via authorRole=user + userId=self).
 */
export default function UserComposeBox({ me, onPosted }) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pending, setPending] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const uploadOne = async (item) => {
    try {
      const attachment = await uploadToR2(item.file, item.kind, {
        asAdmin: false,
        onProgress: (pct) =>
          setPending((p) => p.map((x) => (x.id === item.id ? { ...x, progress: pct } : x))),
      });
      setPending((p) =>
        p.map((x) =>
          x.id === item.id ? { ...x, progress: 100, attachment: { ...attachment, kind: item.kind } } : x,
        ),
      );
    } catch {
      toast.error(`Upload failed: ${item.file.name}`);
      setPending((p) => p.filter((x) => x.id !== item.id));
    }
  };

  const addFiles = (files) => {
    const next = [];
    for (const file of Array.from(files)) {
      const kind = detectKind(file);
      const max = MAX_SIZE_BY_KIND[kind];
      if (file.size > max) {
        toast.error(`${file.name} too large. Max ${Math.round(max / (1024 * 1024))}MB for ${kind}`);
        continue;
      }
      next.push({ id: makePendingId(), file, kind, progress: 0, attachment: null });
    }
    setPending((p) => [...p, ...next]);
    next.forEach((item) => uploadOne(item));
  };

  const reset = () => {
    setTitle("");
    setBody("");
    setPending([]);
    setExpanded(false);
  };

  const submit = async () => {
    if (!title.trim() && !body.trim() && pending.length === 0) {
      toast.error("Add a title, message, or attachment");
      return;
    }
    if (pending.some((x) => !x.attachment)) {
      toast.error("Wait for uploads to finish");
      return;
    }
    setSubmitting(true);
    try {
      await createMyPost({
        title: title.trim(),
        body: body.trim(),
        attachments: pending.map((p) => p.attachment),
      });
      toast.success("Posted to your studio");
      reset();
      onPosted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Collapsed: simple input that expands on focus.
  if (!expanded) {
    return (
      <button
        type="button"
        data-testid="user-compose-collapsed"
        onClick={() => setExpanded(true)}
        className="w-full bg-[#10162a] border border-white/5 rounded-2xl p-4 flex items-center gap-3 text-left hover:border-white/10 transition-colors"
      >
        <Avatar user={me} size="sm" testIdPrefix="user-compose-avatar" />
        <div className="text-white/45 text-sm">
          Share a demo, lyric, question or note with Paul…
        </div>
      </button>
    );
  }

  return (
    <div
      data-testid="user-compose-expanded"
      className="bg-[#10162a] border border-white/10 rounded-2xl p-5"
    >
      <div className="flex items-center gap-3 mb-4">
        <Avatar user={me} size="sm" testIdPrefix="user-compose-avatar" />
        <div className="text-sm text-white/80 font-medium">
          New post · only you and Paul can see this
        </div>
      </div>

      <Input
        data-testid="user-compose-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional) — e.g. Demo idea, Lyrics, Reference track"
        className="bg-[#0b0f1e] border-white/10 text-white mb-3"
      />

      <RichTextEditor
        value={body}
        onChange={setBody}
        placeholder="Write a note, ask a question, or share progress…"
        theme="dark"
        minHeight="100px"
        testIdPrefix="user-compose-body"
      />

      {pending.length > 0 && (
        <div className="space-y-2 mt-3">
          {pending.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 bg-[#0b0f1e] border border-white/5 rounded-lg p-2.5"
            >
              <div className="w-8 h-8 rounded-md bg-paul-yellow/20 text-paul-yellow flex items-center justify-center flex-shrink-0">
                <KindIcon kind={p.kind} className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{p.file.name}</div>
                <div className="h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-paul-yellow transition-all" style={{ width: `${p.progress}%` }} />
                </div>
              </div>
              <div className="text-xs text-white/40 font-mono min-w-[35px] text-right">{p.progress}%</div>
              <button
                type="button"
                onClick={() => setPending((p2) => p2.filter((x) => x.id !== p.id))}
                className="text-white/40 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="gap-2 bg-transparent border-white/10 text-white/80 hover:bg-white/5"
            data-testid="user-compose-attach"
          >
            <Paperclip className="w-4 h-4" />
            Attach
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            data-testid="user-compose-cancel"
            onClick={reset}
            className="text-sm text-white/50 hover:text-white px-3"
          >
            Cancel
          </button>
        </div>
        <Button
          data-testid="user-compose-submit"
          onClick={submit}
          disabled={submitting}
          className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold gap-2"
          style={{ color: "#0b1535" }}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Post
        </Button>
      </div>
      <p className="text-[11px] text-white/30 mt-2">
        Audio ≤ 100MB · Video ≤ 500MB · Images ≤ 20MB · Other ≤ 100MB
      </p>
    </div>
  );
}
